import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { CoachingProvider } from "./context/CoachingContext";
import { NotificationSSEProvider } from "./context/NotificationSSEContext";
import { V2Provider } from "./context/V2Context";
import Layout from "./components/Layout";
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import AuthCallback from "./components/AuthCallBack";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./components/auth/NotFound";
import MobileAppPromptModal from "./components/MobileAppPromptModal";
import RouteFallback, { RouteBoundary } from "./components/RouteFallback";
import { ConfirmProvider } from "./components/ui/confirm";
import { Toaster } from "./components/ui/toast";

// Route-level code splitting (aithlo-frontend#40). The auth screens and the
// app shell (Layout: sidebar, coach thread) stay in the entry chunk so the
// first paint never waits on a second request; every page below is its own
// chunk, fetched the first time it is opened. A page that isn't loaded yet
// shows <RouteFallback /> inside the shell, never a blank screen.
const Profile = lazy(() => import("./components/Profile"));
const Coaching = lazy(() => import("./components/Coaching"));
const ConversationalOnboarding = lazy(() => import("./components/onboarding/ConversationalOnboarding"));
// The onboarding screens one by one, on the dev server only.
const DevOnboardingGallery = import.meta.env.DEV ? lazy(() => import("./components/onboarding/DevOnboardingGallery")) : null;
const DevShellPreview = import.meta.env.DEV ? lazy(() => import("./components/DevShellPreview")) : null;
// Each area's screens on fixture data, on the dev server only.
const devPage = (load) => (import.meta.env.DEV ? lazy(load) : null);
const DEV_PREVIEWS = [
  ["/dev/coach", devPage(() => import("./components/v2/CoachPanelPreview"))],
  ["/dev/charts", devPage(() => import("./components/v2/CoachChartPreview"))],
  ["/dev/stats", devPage(() => import("./components/data/DevStatsPreview"))],
  ["/dev/you", devPage(() => import("./components/you/DevYouPreview"))],
  ["/dev/shift", devPage(() => import("./components/shift/ShiftDevPreview"))],
  ["/dev/app-states", devPage(() => import("./components/appstate/DevAppStatesPreview"))],
  ["/dev/plan", devPage(() => import("./components/plan/PlanDevPreview"))],
];
const Admin = lazy(() => import("./components/Admin"));
const AdminUsers = lazy(() => import("./components/AdminUsers"));
const AdminAIUsage = lazy(() => import("./components/AdminAIUsage"));
const AdminCoach = lazy(() => import("./components/admin/coach/AdminCoach"));
const Activities = lazy(() => import("./components/Activites"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Stats = lazy(() => import("./components/Stats"));
const Shift = lazy(() => import("./components/Shift"));
const Calendar = lazy(() => import("./components/Calendar"));
const Settings = lazy(() => import("./components/Settings"));
const RouteEditorPage = lazy(() => import("./components/RouteEditorPage"));
const Workouts = lazy(() => import("./components/Workouts"));
const SegmentPowerPlanner = lazy(() => import("./components/SegmentPowerPlanner"));
const SeasonPage = lazy(() => import("./components/v2/SeasonScreen").then((m) => ({ default: m.SeasonPage })));

/** A lazy page inside its route guard, with the loading fallback. */
const page = (Page) => (
  <ProtectedRoute>
    <RouteBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Page />
      </Suspense>
    </RouteBoundary>
  </ProtectedRoute>
);

/** Profile, where Coaching used to be a tab: an old `?tab=coaching` link lands on the Coaching page. */
const ProfileRoute = () => (new URLSearchParams(useLocation().search).get("tab") === "coaching"
  ? <Navigate to="/coaching" replace />
  : page(Profile));

function App() {
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <NotificationSSEProvider>
        <CoachingProvider>
        <V2Provider>
        <OnboardingProvider>
        <ConfirmProvider>
        <Toaster />
        <MobileAppPromptModal />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {DevOnboardingGallery && <Route path="/dev/onboarding" element={<DevOnboardingGallery />} />}
          {DevShellPreview && <Route path="/dev/shell" element={<DevShellPreview />} />}
          {import.meta.env.DEV && DEV_PREVIEWS.map(([path, Preview]) => (
            <Route key={path} path={path} element={<Suspense fallback={null}><Preview /></Suspense>} />
          ))}
          <Route path="/oauth/callback" element={<AuthCallback />} />

          <Route
            path="/onboarding"
            element={page(ConversationalOnboarding)}
          />

          <Route path="/" element={<Layout />}>
            <Route
              index
              element={<Navigate to="/dashboard" replace />}
            />

            <Route
              path="profile"
              element={<ProfileRoute />}
            />

            <Route
              path="dashboard"
              element={page(Dashboard)}
            />

            <Route
              path="stats"
              element={page(Stats)}
            />

            {/* Old analytics path → new consolidated Stats page */}
            <Route path="data" element={<Navigate to="/stats" replace />} />

            <Route
              path="segment-planner"
              element={page(SegmentPowerPlanner)}
            />

            <Route
              path="calendar"
              element={page(Calendar)}
            />

            {/* v2: the race screen (phases, week objectives, on-track per goal) */}
            <Route
              path="season"
              element={page(SeasonPage)}
            />

            <Route
              path="workouts"
              element={page(Workouts)}
            />

            <Route
              path="activities"
              element={page(Activities)}
            />

            <Route
              path="shift"
              element={page(Shift)}
            />

            <Route
              path="admin"
              element={page(Admin)}
            />

            <Route
              path="admin/users"
              element={page(AdminUsers)}
            />

            <Route
              path="admin/ai-usage"
              element={page(AdminAIUsage)}
            />

            <Route
              path="admin/coach/*"
              element={page(AdminCoach)}
            />

            <Route
              path="route-editor"
              element={page(RouteEditorPage)}
            />

            <Route
              path="settings"
              element={page(Settings)}
            />

            {/* The nav's Coach: this page beside the coach panel (Sam, 2026-11-02). */}
            <Route
              path="coaching"
              element={page(Coaching)}
            />

          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ConfirmProvider>
        </OnboardingProvider>
        </V2Provider>
        </CoachingProvider>
        </NotificationSSEProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
